import { useTranslation } from 'react-i18next';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';

import { useProjectSiteNavigation } from '@/shared/lib';
import {
  useGetConsignmentLineDetailQuery,
  useCreateConsignmentLine,
  useUpdateConsignmentLine,
  useDeleteConsignmentLine,
  type ConsignmentLineDetailResponse
} from '@/entities/consignments';
import { useGetDangerousGoodsQuery } from '@/entities/lists';
import { selectOptionalFields, selectSubprojectList } from '@/entities/layout';

import { transformFormToRequest, transformResponseToForm, getDefaultFormValues } from '../lib';
import { getConsignmentLineDetailsFormSchema } from './validation';
import { getProductCategoryOptions, getInventoryUnitOptions, getHandlingUnitOptions } from './constants';
import type { ConsignmentLineDetailsFormValues } from './types';
import { useSelector } from 'react-redux';

interface UseConsignmentLineDetailsFormProps {
  lineId?: string;
  consignmentId?: string;
  mode?: 'create' | 'edit' | 'view';
  onSuccess?: (lineDetails: ConsignmentLineDetailResponse) => void;
  onDeleteSuccess?: () => void;
  onClose: () => void;
}

export const useConsignmentLineDetailsForm = ({
  lineId,
  consignmentId,
  mode = 'view',
  onSuccess,
  onDeleteSuccess,
  onClose
}: UseConsignmentLineDetailsFormProps) => {
  const [isResetting, setIsResetting] = useState(false);
  const { projectId, siteId } = useProjectSiteNavigation();
  const { t } = useTranslation();

  const optionalFields = useSelector(selectOptionalFields(projectId, siteId));
  const subprojectList = useSelector(selectSubprojectList(projectId, siteId));

  const productCategoryOptions = useMemo(() => getProductCategoryOptions(t), [t]);
  const inventoryUnitOptions = useMemo(() => getInventoryUnitOptions(t), [t]);
  const handlingUnitOptions = useMemo(() => getHandlingUnitOptions(t), [t]);

  const isEditMode = mode === 'edit';
  const isCreateMode = mode === 'create';
  const isViewMode = mode === 'view';

  const consignmentLineDetailsFormSchema = useMemo(
    () => getConsignmentLineDetailsFormSchema(t, subprojectList.length > 1, optionalFields),
    [t, subprojectList.length, optionalFields]
  );

  const defaultValues = useMemo(() => getDefaultFormValues(), []);

  const form = useForm<ConsignmentLineDetailsFormValues>({
    resolver: zodResolver(consignmentLineDetailsFormSchema),
    defaultValues
  });

  const { watch, reset, handleSubmit: formHandleSubmit } = form;

  const {
    data: lineDetails,
    isLoading,
    isFetching
  } = useGetConsignmentLineDetailQuery(
    { lineId, projectId, siteId },
    {
      skip: !lineId || !projectId || !siteId || isCreateMode
    }
  );

  const { data: dangerousGoodsData } = useGetDangerousGoodsQuery(
    { projectId, siteId },
    { skip: !projectId || !siteId }
  );

  const dangerousGoodsOptions = useMemo(() => {
    if (!dangerousGoodsData?.items) return [];
    return dangerousGoodsData.items.map((item) => ({
      value: item.id.toString(),
      label: `${item.dgUNId} - ${item.description}`
    }));
  }, [dangerousGoodsData]);

  const subProjectOptions = useMemo(
    () =>
      subprojectList.map((subproject) => ({
        value: subproject.description,
        label: subproject.description
      })),
    [subprojectList]
  );

  const { createConsignmentLine, isLoading: isCreating } = useCreateConsignmentLine({
    onSuccess: (lineDetails) => {
      onSuccess?.(lineDetails);
    },
    onError: (error) => {
      console.error('Failed to create consignment line:', error);
    }
  });

  const { updateConsignmentLine, isLoading: isUpdating } = useUpdateConsignmentLine({
    onSuccess: (lineDetails) => {
      onSuccess?.(lineDetails);
      onClose();
    },
    onError: (error) => {
      console.error('Failed to update consignment line:', error);
    }
  });

  const { deleteConsignmentLine, isLoading: isDeleting } = useDeleteConsignmentLine({
    onSuccess: () => {
      onDeleteSuccess?.();
      onClose();
    },
    onError: (error) => {
      console.error('Failed to delete consignment line:', error);
    }
  });

  const handleSubmit = useCallback(
    async (data: ConsignmentLineDetailsFormValues) => {
      const requestData = transformFormToRequest(data, consignmentId);

      if (isCreateMode) {
        await createConsignmentLine({ projectId, siteId }, requestData);
        reset();
      } else if (isEditMode && lineId) {
        await updateConsignmentLine({ id: lineId, projectId, siteId }, requestData);
      }
    },
    [
      consignmentId,
      createConsignmentLine,
      reset,
      isCreateMode,
      isEditMode,
      lineId,
      projectId,
      siteId,
      updateConsignmentLine
    ]
  );

  const handleDelete = useCallback(async () => {
    if (!lineId) return;
    await deleteConsignmentLine({ id: lineId, projectId, siteId, consignmentId });
  }, [lineId, deleteConsignmentLine, projectId, siteId, consignmentId]);

  // Calculate conversion rates info
  const getConversionRateInfo = (handlingUnitTypeId: string, inventoryUnit: string) => {
    const conversions: Record<string, string> = {
      'Box/Carton-Each': '1 Each per Box/Carton',
      'Box/Carton-Litre': '0.1kg per Litre',
      'Bag/Sack-Bulk (kg)': '0.5kg per Bulk (kg)',
      'None/Loose-Litre': '100000 Litre with no handling'
    };

    const key = `${handlingUnitTypeId}-${inventoryUnit}`;
    return conversions[key] || null;
  };

  const conversionRateInfo = getConversionRateInfo(watch('handlingUnitTypeId'), watch('productLtuId'));

  useEffect(() => {
    if (lineDetails && !isCreateMode) {
      setIsResetting(true);
      setTimeout(() => {
        const formValues = transformResponseToForm(lineDetails);
        reset(formValues);
        setIsResetting(false);
      }, 0);
    }
  }, [lineDetails, isCreateMode, reset]);

  return {
    // Form state
    form,
    lineDetails,

    // Select options
    productCategoryOptions,
    inventoryUnitOptions,
    handlingUnitOptions,
    dangerousGoodsOptions,

    // Computed values
    conversionRateInfo,
    optionalFields,
    subProjectOptions,

    // Loading states
    isLoading,
    isFetching,
    isSaving: isCreating || isUpdating,
    isDeleting,
    isResetting,

    // Mode flags
    isViewMode,
    isEditMode,
    isCreateMode,

    // Actions
    handleSubmit: formHandleSubmit(handleSubmit),
    handleDelete,
    handleClose: onClose
  };
};
